import { jsx } from "panel";
import { delayedAttrRemove } from "panel/build/component-utils/hook-helpers";
import { ButtonEl } from "common/components/button";
import { SvgIconEl } from "common/components/svg-icon";
import { DropMenuEl } from "common/components/drop-menu";
import { ItemsMenuEl } from "common/components/items-menu";
import { TooltipEl } from "common/components/tooltip";
import { ContextPanelEl } from "common/widgets/context-panel";
import { ButtonTheme } from "common/components/button/constants";
import { hasSlot } from "common/util/dom";
import { queryBuilderUtils } from "../query-builder/util";
import { MenuType } from "./types";
function SelectorLabelEl({ comp }) {
  const isUnsaved = comp.helpers.isUnsavedCustomEntity();
  const blockSelectorLabel = comp.helpers.showInput() ? comp.helpers.selectedLabel() || comp.attr(`label`) : comp.attr(`label`);
  const selections = comp.helpers.getSelections();
  if (comp.attr(`highlight-selections`) && (selections == null ? void 0 : selections.length)) {
    const indices = /* @__PURE__ */ new Set();
    const words = blockSelectorLabel.split(` `);
    for (const selection of selections) {
      const targetWords = selection.split(` `);
      for (let i = 0; i < words.length; i++) {
        if (words.slice(i, i + targetWords.length).join(` `) === targetWords.join(` `)) {
          for (let j = i; j < i + targetWords.length; j++) {
            indices.add(j);
          }
          break;
        }
      }
    }
    return /* @__PURE__ */ jsx("span", {
      class: { "is-unsaved": isUnsaved }
    }, words.map((word, idx) => {
      if (indices.has(idx)) {
        return /* @__PURE__ */ jsx("b", null, word, " ");
      }
      return `${word} `;
    }));
  }
  return /* @__PURE__ */ jsx("span", {
    id: `blockSelector`,
    class: { "is-unsaved": isUnsaved }
  }, blockSelectorLabel + (isUnsaved ? `*` : ``));
}
function BlockSelectorEl() {
  var _a, _b;
  const { isMenuOpen } = this.state;
  const selectorTheme = this.helpers.getSelectorTheme();
  const prefixLabel = this.helpers.getPrefixLabel();
  const removeButtonEl = this.attr(`removable`) && /* @__PURE__ */ jsx("div", {
    sel: ".mp-query-block-remove-button",
    on: { click: this.helpers.handleRemove }
  }, /* @__PURE__ */ jsx(SvgIconEl, {
    attrs: { icon: `circle-clear` }
  }));
  const blockSelectorLabel = this.helpers.showInput() ? this.helpers.selectedLabel() || this.attr(`label`) : this.attr(`label`);
  const blockBrowserTooltipLabel = this.helpers.selectedBrowserTooltipLabel();
  const blockSelectorLeftIcon = (_a = this.attr(`selector-icon`)) != null ? _a : this.helpers.selectedLeftIcon();
  const blockSelectorRightIcon = (_b = this.attr(`selector-right-icon`)) != null ? _b : this.helpers.selectedRightIcon();
  const allowedButtonThemes = [ButtonTheme.Select, ButtonTheme.QbSubitem];
  if (this.attr(`selector-type`) === `button` || allowedButtonThemes.includes(this.attr(`theme`))) {
    return /* @__PURE__ */ jsx(ButtonEl, {
      sel: ".truncated",
      key: JSON.stringify(this.attr(`selected`)) + blockSelectorLabel,
      attrs: {
        disabled: this.attr(`disabled`),
        icon: blockSelectorLeftIcon || ``,
        theme: selectorTheme,
        size: this.attr(`button-size`),
        "not-focusable": this.helpers.isReadOnly(),
        pressed: isMenuOpen,
        readonly: this.helpers.isReadOnly(),
        elref: `blockMenuLabelIcon`,
        ...blockBrowserTooltipLabel ? {
          title: blockBrowserTooltipLabel
        } : {}
      },
      hook: {
        insert: ({ elm }) => this.helpers.onAutofocusableElAdded(elm)
      }
    }, hasSlot(this, `selector-icon`) && /* @__PURE__ */ jsx("slot", {
      attrs: { name: `selector-icon` }
    }), /* @__PURE__ */ jsx("div", {
      class: {
        "has-right-icon": !!blockSelectorRightIcon
      },
      attrs: { elref: `blockMenuLabel` }
    }, this.params.labelRenderer ? this.params.labelRenderer() : /* @__PURE__ */ jsx(null, null, prefixLabel && /* @__PURE__ */ jsx("span", {
      sel: ".label-prefix"
    }, prefixLabel), /* @__PURE__ */ jsx(SelectorLabelEl, {
      comp: this
    }))), blockSelectorRightIcon && /* @__PURE__ */ jsx(SvgIconEl, {
      attrs: { elref: `rightIcon`, icon: blockSelectorRightIcon }
    }), removeButtonEl);
  } else {
    return /* @__PURE__ */ jsx("div", {
      sel: ".mp-query-menu-label",
      class: {
        "is-disabled": this.attr(`disabled`),
        "is-editing": this.helpers.showInput() && isMenuOpen,
        [`is-theme-${selectorTheme}`]: true
      },
      attrs: {
        elref: `blockMenuLabel`
      }
    }, blockSelectorLeftIcon && /* @__PURE__ */ jsx(SvgIconEl, {
      attrs: { icon: blockSelectorLeftIcon, elref: `blockMenuLabelIcon` }
    }), blockSelectorLabel, blockSelectorRightIcon && /* @__PURE__ */ jsx(SvgIconEl, {
      attrs: { icon: blockSelectorRightIcon }
    }), removeButtonEl);
  }
}
export function template() {
  const hasNewUI = queryBuilderUtils.isCoreReport();
  const { getSortedMenuSections, isHeaderMenuOpen, isMenuOpen, openedMenuForModal, tooltipData } = this.state;
  const selectorType = this.attr(`selector-type`);
  const selectorTheme = this.helpers.getSelectorTheme();
  const prefixLabel = this.helpers.getPrefixLabel();
  const headerSegments = this.helpers.getHeaderSegments();
  const tooltipAttrs = this.attr(`tooltip`);
  return /* @__PURE__ */ jsx("div", {
    sel: ".mp-query-block",
    class: {
      "has-prefix-label": prefixLabel,
      "for-new-ui": hasNewUI,
      "has-header-segments": headerSegments.length,
      [`mp-query-theme-${selectorTheme}`]: true
    },
    on: {
      openContextMenu: this.helpers.propagateEvent,
      closeContextMenu: this.helpers.propagateEvent,
      mousedown: this.helpers.handleMouseDown
    }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".mp-query-block-header-container"
  }, headerSegments.map(
    (segment) => segment.type === `text` ? /* @__PURE__ */ jsx("div", {
      sel: ".mp-query-block-header-segment-label"
    }, segment.value) : segment.type === `select` ? /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
      sel: ".mp-query-block-header-drop-menu"
    }, /* @__PURE__ */ jsx(DropMenuEl, {
      attrs: { open: isHeaderMenuOpen },
      on: { change: this.helpers.handleHeaderMenuVisibilityChange }
    }, /* @__PURE__ */ jsx(ItemsMenuEl, {
      attrs: {
        sections: this.helpers.getHeaderMenuSection(segment)
      },
      on: { clickItem: this.helpers.handleHeaderSelectionChanged }
    }))), /* @__PURE__ */ jsx("div", {
      sel: ".mp-query-block-header-segment-selector-label",
      on: { click: this.helpers.handleHeaderSelectClicked }
    }, segment.selectedItem.label, /* @__PURE__ */ jsx(SvgIconEl, {
      attrs: { icon: `caret-down` }
    }))) : null
  ), this.attr(`help-text`) ? /* @__PURE__ */ jsx("div", {
    sel: ".header-info"
  }, /* @__PURE__ */ jsx(SvgIconEl, {
    attrs: { icon: `info` }
  }), /* @__PURE__ */ jsx(TooltipEl, {
    sel: ".help-text"
  }, this.attr(`help-text`))) : null), selectorType !== `button` && prefixLabel ? /* @__PURE__ */ jsx("div", {
    sel: ".mp-query-block-unit",
    attrs: { elref: `blockUnitLabel` }
  }, prefixLabel) : null, /* @__PURE__ */ jsx("div", {
    sel: ".mp-query-block-selector",
    class: { "mp-query-editing": this.helpers.showInput() && isMenuOpen },
    on: {
      click: !this.helpers.isReadOnly() && this.helpers.handleLabelClick
    },
    attrs: {
      elref: `selectorLabel`
    }
  }, tooltipData && !isMenuOpen && !openedMenuForModal ? this.helpers.renderContextPanelFromMenu() ? this.helpers.renderContextPanelFromMenu() : /* @__PURE__ */ jsx(ContextPanelEl, {
    hook: { remove: delayedAttrRemove(`tooltip-data`, null) },
    attrs: {
      ...this.helpers.getContextPanelAttrs(),
      "tooltip-data": tooltipData
    },
    props: { tooltipPositionParentElement: this._contextPanelParentElement },
    on: {
      hoverIn: this.helpers.handleContextPanelHoverIn,
      hoverOut: this.helpers.handleContextPanelHoverOut,
      tooltipContextAction: this.helpers.handleTooltipContextAction,
      tooltipContextEditDescription: this.helpers.handlePropagatedtooltipContextEditDescription
    }
  }) : !isMenuOpen && tooltipAttrs ? /* @__PURE__ */ jsx(TooltipEl, {
    attrs: Object.assign(
      {
        size: `small`,
        placement: `right`
      },
      tooltipAttrs
    )
  }) : null, /* @__PURE__ */ jsx("div", {
    sel: ".mp-query-block-selector-inner",
    attrs: {
      elref: `selectorLabelInner`
    },
    on: {
      mouseenter: this.helpers.handleLabelMouseEnter,
      mouseleave: this.helpers.handleLabelMouseLeave
    }
  }, BlockSelectorEl.call(this), this.helpers.renderSelectedDateTooltipIfNeeded()), /* @__PURE__ */ jsx("div", {
    sel: ".mp-query-drop-menu-container",
    class: {
      "for-new-ui": hasNewUI,
      [`for-${this.attr(`type`)}-menu`]: true
    }
  }, /* @__PURE__ */ jsx(DropMenuEl, {
    class: {
      "custom-width": this.helpers.menuHasCustomWidth()
    },
    attrs: {
      open: isMenuOpen,
      direction: this.attr(`menu-alignment`),
      "prevent-close": this.state.isModalOpen,
      "prevent-close-selectors": [
        `#chmln`,
        ...this.attr(`type`) === MenuType.Properties ? [`.mp-query-block-selector`] : [],
        `mp-custom-property-modal`,
        `mp-tooltip`
      ],
      ...hasNewUI && { offset: this.attr(`menu-offset`) }
    },
    on: {
      change: this.helpers.handleMenuChange,
      click: (ev) => ev.stopPropagation()
    }
  }, this.child(this.childMenuComponentName, {
    attrs: this.helpers.getChildMenuAttributes(),
    params: this.helpers.getChildMenuParams(),
    key: this.childMenuComponentName,
    on: {
      selectAll: this.helpers.handleSelectAll,
      select: this.helpers.handleSelect,
      create: this.helpers.propagateEvent,
      delete: this.helpers.handleDelete,
      cancel: this.helpers.handleCancel,
      closedModal: this.helpers.handleChildMenuClosedModal,
      openedModal: this.helpers.handleChildMenuOpenedModal,
      tooltipContextEditDescription: this.helpers.handlePropagatedtooltipContextEditDescription
    },
    hook: { insert: this.helpers.menuInserted },
    props: {
      getSortedMenuSections
    }
  })))));
}
